// Communication Board Types

export type CellType = "category" | "utterance";

export interface UtteranceCell {
  id: string;
  type: "utterance";
  text: string;
  icon?: string; // Lucide icon name
  emoji?: string; // Emoji fallback
  color: string; // Color ID from boardColors
  showEmoji?: boolean; // Whether to display emoji (default true)
}

export interface CategoryCell {
  id: string;
  type: "category";
  name: string;
  icon?: string; // Lucide icon name
  emoji?: string; // Emoji fallback
  color: string; // Color ID from boardColors
  utterances: UtteranceCell[]; // Nested utterances
  showEmoji?: boolean; // Whether to display emoji (default true)
}

export type BoardCell = CategoryCell | UtteranceCell | null;

export interface CommunicationBoardData {
  id: string;
  name: string;
  icon?: string;
  emoji?: string;
  rows: number;
  columns: number;
  cells: BoardCell[][];
  createdAt: string;
  updatedAt: string;
}

// Import new default board
import { getNewDefaultBoard, type SupportedLanguage } from "./communicationBoardDefaults";
import { migrateCommunicationBoards } from "./communicationBoardMigrations";

// Sentence Builder Board - allows building sentences from individual words
export function getSentenceBuilderBoard(language: "he" | "en" = "he", gender: "male" | "female" | "neutral" = "neutral"): CommunicationBoardData {
  if (language === "en") {
    return getSentenceBuilderBoardEn();
  }
  return getSentenceBuilderBoardHe(gender);
}

// English Sentence Builder Board - optimized for English grammar
function getSentenceBuilderBoardEn(): CommunicationBoardData {
  // Word type colors (based on AAC standards):
  // purple - pronouns
  // green - verbs  
  // gray - connectors/little words
  // orange - descriptors/adjectives
  // blue - questions
  // teal - responses/time
  // pink - feelings
  // yellow - categories

  // 8 columns x 5 rows - optimized for tablet landscape
  
  // Row 1: Pronouns + Yes/No + Feelings Category
  const row1: BoardCell[] = [
    { id: "sb-en-1-1", type: "utterance", text: "I", color: "purple" },
    { id: "sb-en-1-2", type: "utterance", text: "you", color: "purple" },
    { id: "sb-en-1-3", type: "utterance", text: "he", color: "purple" },
    { id: "sb-en-1-4", type: "utterance", text: "she", color: "purple" },
    { id: "sb-en-1-5", type: "utterance", text: "it", color: "purple" },
    { id: "sb-en-1-6", type: "utterance", text: "yes", color: "teal" },
    { id: "sb-en-1-7", type: "utterance", text: "no", color: "teal" },
    {
      id: "sb-en-cat-feelings",
      type: "category",
      name: "Feelings",
      color: "pink",
      utterances: [
        { id: "sb-en-f-1", type: "utterance", text: "happy", color: "pink" },
        { id: "sb-en-f-2", type: "utterance", text: "sad", color: "pink" },
        { id: "sb-en-f-3", type: "utterance", text: "angry", color: "pink" },
        { id: "sb-en-f-4", type: "utterance", text: "scared", color: "pink" },
        { id: "sb-en-f-5", type: "utterance", text: "tired", color: "pink" },
        { id: "sb-en-f-6", type: "utterance", text: "calm", color: "pink" },
        { id: "sb-en-f-7", type: "utterance", text: "excited", color: "pink" },
        { id: "sb-en-f-8", type: "utterance", text: "worried", color: "pink" },
      ]
    },
  ];

  // Row 2: Core verbs + connectors + Needs Category
  const row2: BoardCell[] = [
    { id: "sb-en-2-1", type: "utterance", text: "want", color: "green" },
    { id: "sb-en-2-2", type: "utterance", text: "need", color: "green" },
    { id: "sb-en-2-3", type: "utterance", text: "can", color: "green" },
    { id: "sb-en-2-4", type: "utterance", text: "like", color: "green" },
    { id: "sb-en-2-5", type: "utterance", text: "to", color: "gray" },
    { id: "sb-en-2-6", type: "utterance", text: "the", color: "gray" },
    { id: "sb-en-2-7", type: "utterance", text: "a", color: "gray" },
    {
      id: "sb-en-cat-needs",
      type: "category",
      name: "Needs",
      color: "orange",
      utterances: [
        { id: "sb-en-n-1", type: "utterance", text: "food", color: "orange" },
        { id: "sb-en-n-2", type: "utterance", text: "drink", color: "orange" },
        { id: "sb-en-n-3", type: "utterance", text: "bathroom", color: "orange" },
        { id: "sb-en-n-4", type: "utterance", text: "rest", color: "orange" },
        { id: "sb-en-n-5", type: "utterance", text: "help", color: "orange" },
        { id: "sb-en-n-6", type: "utterance", text: "medicine", color: "orange" },
        { id: "sb-en-n-7", type: "utterance", text: "hurts", color: "orange" },
        { id: "sb-en-n-8", type: "utterance", text: "cold", color: "orange" },
      ]
    },
  ];

  // Row 3: Action verbs + connectors + Places Category
  const row3: BoardCell[] = [
    { id: "sb-en-3-1", type: "utterance", text: "go", color: "green" },
    { id: "sb-en-3-2", type: "utterance", text: "come", color: "green" },
    { id: "sb-en-3-3", type: "utterance", text: "do", color: "green" },
    { id: "sb-en-3-4", type: "utterance", text: "give", color: "green" },
    { id: "sb-en-3-5", type: "utterance", text: "get", color: "green" },
    { id: "sb-en-3-6", type: "utterance", text: "my", color: "gray" },
    { id: "sb-en-3-7", type: "utterance", text: "your", color: "gray" },
    {
      id: "sb-en-cat-places",
      type: "category",
      name: "Places",
      color: "teal",
      utterances: [
        { id: "sb-en-p-1", type: "utterance", text: "home", color: "teal" },
        { id: "sb-en-p-2", type: "utterance", text: "room", color: "teal" },
        { id: "sb-en-p-3", type: "utterance", text: "kitchen", color: "teal" },
        { id: "sb-en-p-4", type: "utterance", text: "outside", color: "teal" },
        { id: "sb-en-p-5", type: "utterance", text: "upstairs", color: "teal" },
        { id: "sb-en-p-6", type: "utterance", text: "downstairs", color: "teal" },
        { id: "sb-en-p-7", type: "utterance", text: "living room", color: "teal" },
        { id: "sb-en-p-8", type: "utterance", text: "bedroom", color: "teal" },
      ]
    },
  ];

  // Row 4: More verbs + descriptors + People Category
  const row4: BoardCell[] = [
    { id: "sb-en-4-1", type: "utterance", text: "think", color: "green" },
    { id: "sb-en-4-2", type: "utterance", text: "feel", color: "green" },
    { id: "sb-en-4-3", type: "utterance", text: "see", color: "green" },
    { id: "sb-en-4-4", type: "utterance", text: "hear", color: "green" },
    { id: "sb-en-4-5", type: "utterance", text: "am", color: "green" },
    { id: "sb-en-4-6", type: "utterance", text: "good", color: "orange" },
    { id: "sb-en-4-7", type: "utterance", text: "more", color: "orange" },
    {
      id: "sb-en-cat-people",
      type: "category",
      name: "People",
      color: "purple",
      utterances: [
        { id: "sb-en-pe-1", type: "utterance", text: "mom", color: "purple" },
        { id: "sb-en-pe-2", type: "utterance", text: "dad", color: "purple" },
        { id: "sb-en-pe-3", type: "utterance", text: "caregiver", color: "purple" },
        { id: "sb-en-pe-4", type: "utterance", text: "doctor", color: "purple" },
        { id: "sb-en-pe-5", type: "utterance", text: "friend", color: "purple" },
        { id: "sb-en-pe-6", type: "utterance", text: "someone", color: "purple" },
        { id: "sb-en-pe-7", type: "utterance", text: "everyone", color: "purple" },
        { id: "sb-en-pe-8", type: "utterance", text: "no one", color: "purple" },
      ]
    },
  ];

  // Row 5: Questions + time + Time Category
  const row5: BoardCell[] = [
    { id: "sb-en-5-1", type: "utterance", text: "what", color: "blue" },
    { id: "sb-en-5-2", type: "utterance", text: "where", color: "blue" },
    { id: "sb-en-5-3", type: "utterance", text: "now", color: "teal" },
    { id: "sb-en-5-4", type: "utterance", text: "later", color: "teal" },
    { id: "sb-en-5-5", type: "utterance", text: "here", color: "teal" },
    { id: "sb-en-5-6", type: "utterance", text: "there", color: "teal" },
    { id: "sb-en-5-7", type: "utterance", text: "thanks", color: "teal" },
    {
      id: "sb-en-cat-time",
      type: "category",
      name: "Time",
      color: "blue",
      utterances: [
        { id: "sb-en-t-1", type: "utterance", text: "today", color: "blue" },
        { id: "sb-en-t-2", type: "utterance", text: "tomorrow", color: "blue" },
        { id: "sb-en-t-3", type: "utterance", text: "yesterday", color: "blue" },
        { id: "sb-en-t-4", type: "utterance", text: "morning", color: "blue" },
        { id: "sb-en-t-5", type: "utterance", text: "evening", color: "blue" },
        { id: "sb-en-t-6", type: "utterance", text: "night", color: "blue" },
        { id: "sb-en-t-7", type: "utterance", text: "always", color: "blue" },
        { id: "sb-en-t-8", type: "utterance", text: "never", color: "blue" },
      ]
    },
  ];

  return {
    id: "sentence-builder",
    name: "Sentence Builder",
    icon: "Layers",
    rows: 5,
    columns: 8,
    cells: [row1, row2, row3, row4, row5],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function getSentenceBuilderBoardHe(gender: "male" | "female" | "neutral" = "neutral"): CommunicationBoardData {
  // Gender-aware verbs
  const isFemale = gender === "female";
  
  // Word type colors (based on AAC standards):
  // purple - pronouns (כינויים)
  // green - verbs (פעלים) 
  // gray - connectors/little words (מילות קישור)
  // orange - descriptors (תארים)
  // blue - questions (שאלות)
  // teal - responses/time (תגובות/זמן)
  // pink - feelings (רגשות)
  // yellow - categories that expand

  // 8 columns x 5 rows - optimized for tablet landscape
  
  // Row 1: Pronouns + Yes/No + Category
  const row1: BoardCell[] = [
    { id: "sb-1-1", type: "utterance", text: "אני", color: "purple" },
    { id: "sb-1-2", type: "utterance", text: "שלי", color: "purple" },
    { id: "sb-1-3", type: "utterance", text: "הוא", color: "purple" },
    { id: "sb-1-4", type: "utterance", text: "היא", color: "purple" },
    { id: "sb-1-5", type: "utterance", text: "זה", color: "gray" },
    { id: "sb-1-6", type: "utterance", text: "כן", color: "teal" },
    { id: "sb-1-7", type: "utterance", text: "לא", color: "teal" },
    {
      id: "sb-cat-feelings",
      type: "category",
      name: "רגשות",
      color: "pink",
      utterances: [
        { id: "sb-f-1", type: "utterance", text: isFemale ? "שמחה" : "שמח", color: "pink" },
        { id: "sb-f-2", type: "utterance", text: isFemale ? "עצובה" : "עצוב", color: "pink" },
        { id: "sb-f-3", type: "utterance", text: isFemale ? "כועסת" : "כועס", color: "pink" },
        { id: "sb-f-4", type: "utterance", text: isFemale ? "מפחדת" : "מפחד", color: "pink" },
        { id: "sb-f-5", type: "utterance", text: isFemale ? "עייפה" : "עייף", color: "pink" },
        { id: "sb-f-6", type: "utterance", text: isFemale ? "רגועה" : "רגוע", color: "pink" },
        { id: "sb-f-7", type: "utterance", text: isFemale ? "מתרגשת" : "מתרגש", color: "pink" },
        { id: "sb-f-8", type: "utterance", text: isFemale ? "מודאגת" : "מודאג", color: "pink" },
      ]
    },
  ];

  // Row 2: Core verbs + connectors + Category
  const row2: BoardCell[] = [
    { id: "sb-2-1", type: "utterance", text: isFemale ? "רוצה" : "רוצה", color: "green" },
    { id: "sb-2-2", type: "utterance", text: isFemale ? "צריכה" : "צריך", color: "green" },
    { id: "sb-2-3", type: "utterance", text: isFemale ? "יכולה" : "יכול", color: "green" },
    { id: "sb-2-4", type: "utterance", text: isFemale ? "אוהבת" : "אוהב", color: "green" },
    { id: "sb-2-5", type: "utterance", text: "עם", color: "gray" },
    { id: "sb-2-6", type: "utterance", text: "ל", color: "gray" },
    { id: "sb-2-7", type: "utterance", text: "ב", color: "gray" },
    {
      id: "sb-cat-needs",
      type: "category",
      name: "צרכים",
      color: "orange",
      utterances: [
        { id: "sb-n-1", type: "utterance", text: "אוכל", color: "orange" },
        { id: "sb-n-2", type: "utterance", text: "שתייה", color: "orange" },
        { id: "sb-n-3", type: "utterance", text: "שירותים", color: "orange" },
        { id: "sb-n-4", type: "utterance", text: "מנוחה", color: "orange" },
        { id: "sb-n-5", type: "utterance", text: "עזרה", color: "orange" },
        { id: "sb-n-6", type: "utterance", text: "תרופה", color: "orange" },
        { id: "sb-n-7", type: "utterance", text: "כואב לי", color: "orange" },
        { id: "sb-n-8", type: "utterance", text: "קר לי", color: "orange" },
      ]
    },
  ];

  // Row 3: Action verbs + connectors + Category
  const row3: BoardCell[] = [
    { id: "sb-3-1", type: "utterance", text: isFemale ? "הולכת" : "הולך", color: "green" },
    { id: "sb-3-2", type: "utterance", text: isFemale ? "באה" : "בא", color: "green" },
    { id: "sb-3-3", type: "utterance", text: isFemale ? "עושה" : "עושה", color: "green" },
    { id: "sb-3-4", type: "utterance", text: isFemale ? "נותנת" : "נותן", color: "green" },
    { id: "sb-3-5", type: "utterance", text: isFemale ? "לוקחת" : "לוקח", color: "green" },
    { id: "sb-3-6", type: "utterance", text: "את", color: "gray" },
    { id: "sb-3-7", type: "utterance", text: "של", color: "gray" },
    {
      id: "sb-cat-places",
      type: "category",
      name: "מקומות",
      color: "teal",
      utterances: [
        { id: "sb-p-1", type: "utterance", text: "הבית", color: "teal" },
        { id: "sb-p-2", type: "utterance", text: "החדר", color: "teal" },
        { id: "sb-p-3", type: "utterance", text: "המטבח", color: "teal" },
        { id: "sb-p-4", type: "utterance", text: "בחוץ", color: "teal" },
        { id: "sb-p-5", type: "utterance", text: "למעלה", color: "teal" },
        { id: "sb-p-6", type: "utterance", text: "למטה", color: "teal" },
        { id: "sb-p-7", type: "utterance", text: "הסלון", color: "teal" },
        { id: "sb-p-8", type: "utterance", text: "חדר שינה", color: "teal" },
      ]
    },
  ];

  // Row 4: More verbs + descriptors + Category
  const row4: BoardCell[] = [
    { id: "sb-4-1", type: "utterance", text: isFemale ? "חושבת" : "חושב", color: "green" },
    { id: "sb-4-2", type: "utterance", text: isFemale ? "מרגישה" : "מרגיש", color: "green" },
    { id: "sb-4-3", type: "utterance", text: isFemale ? "רואה" : "רואה", color: "green" },
    { id: "sb-4-4", type: "utterance", text: isFemale ? "שומעת" : "שומע", color: "green" },
    { id: "sb-4-5", type: "utterance", text: isFemale ? "עוזרת" : "עוזר", color: "green" },
    { id: "sb-4-6", type: "utterance", text: "טוב", color: "orange" },
    { id: "sb-4-7", type: "utterance", text: "עוד", color: "orange" },
    {
      id: "sb-cat-people",
      type: "category",
      name: "אנשים",
      color: "purple",
      utterances: [
        { id: "sb-pe-1", type: "utterance", text: "אמא", color: "purple" },
        { id: "sb-pe-2", type: "utterance", text: "אבא", color: "purple" },
        { id: "sb-pe-3", type: "utterance", text: isFemale ? "המטפלת" : "המטפל", color: "purple" },
        { id: "sb-pe-4", type: "utterance", text: isFemale ? "הרופאה" : "הרופא", color: "purple" },
        { id: "sb-pe-5", type: "utterance", text: isFemale ? "חברה" : "חבר", color: "purple" },
        { id: "sb-pe-6", type: "utterance", text: "מישהו", color: "purple" },
        { id: "sb-pe-7", type: "utterance", text: "כולם", color: "purple" },
        { id: "sb-pe-8", type: "utterance", text: "אף אחד", color: "purple" },
      ]
    },
  ];

  // Row 5: Questions + time + Category
  const row5: BoardCell[] = [
    { id: "sb-5-1", type: "utterance", text: "מה", color: "blue" },
    { id: "sb-5-2", type: "utterance", text: "מי", color: "blue" },
    { id: "sb-5-3", type: "utterance", text: "עכשיו", color: "teal" },
    { id: "sb-5-4", type: "utterance", text: "אחר כך", color: "teal" },
    { id: "sb-5-5", type: "utterance", text: "פה", color: "teal" },
    { id: "sb-5-6", type: "utterance", text: "שם", color: "teal" },
    { id: "sb-5-7", type: "utterance", text: "תודה", color: "teal" },
    {
      id: "sb-cat-time",
      type: "category",
      name: "זמן",
      color: "blue",
      utterances: [
        { id: "sb-t-1", type: "utterance", text: "היום", color: "blue" },
        { id: "sb-t-2", type: "utterance", text: "מחר", color: "blue" },
        { id: "sb-t-3", type: "utterance", text: "אתמול", color: "blue" },
        { id: "sb-t-4", type: "utterance", text: "בבוקר", color: "blue" },
        { id: "sb-t-5", type: "utterance", text: "בערב", color: "blue" },
        { id: "sb-t-6", type: "utterance", text: "בלילה", color: "blue" },
        { id: "sb-t-7", type: "utterance", text: "תמיד", color: "blue" },
        { id: "sb-t-8", type: "utterance", text: "אף פעם", color: "blue" },
      ]
    },
  ];

  return {
    id: "sentence-builder",
    name: "בונה משפטים",
    icon: "Layers",
    rows: 5,
    columns: 8,
    cells: [row1, row2, row3, row4, row5],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// Convert existing categories to the new format - now uses new defaults
export function getDefaultBoard(language: "he" | "en" = "he", gender: "male" | "female" | "neutral" = "neutral"): CommunicationBoardData {
  // Use the new default board structure with gender
  return getNewDefaultBoard(language as SupportedLanguage, gender);
}

// Storage helpers
// Use language-specific storage keys so boards are stored separately per language
// v30 - added gender-aware categories (צריכה, מרגישה, חושבת, אוהבת, לא אוהבת) and meal items
const BOARDS_STORAGE_KEY_PREFIX = "communicationBoards_v31"; // v31: pronoun-you-f en changed to "we"

function getBoardsStorageKey(language: string): string {
  return `${BOARDS_STORAGE_KEY_PREFIX}_${language}`;
}

export function loadBoards(language: "he" | "en" | "ru" | "ar" = "he", gender: "male" | "female" | "neutral" = "neutral"): CommunicationBoardData[] {
  try {
    const saved = localStorage.getItem(getBoardsStorageKey(language));
    if (saved) {
      const parsed = JSON.parse(saved) as CommunicationBoardData[];
      const { boards: migrated, changed } = migrateCommunicationBoards(parsed, language, gender);
      if (changed) {
        // Persist migration so subsequent loads are consistent
        saveBoards(migrated, language);
      }
      return migrated;
    }
  } catch (e) {
    console.error("Failed to load boards:", e);
  }
  return [getNewDefaultBoard(language, gender)];
}

export function saveBoards(boards: CommunicationBoardData[], language: "he" | "en" | "ru" | "ar" = "he") {
  localStorage.setItem(getBoardsStorageKey(language), JSON.stringify(boards));
}

export function saveBoard(board: CommunicationBoardData, language: "he" | "en" | "ru" | "ar" = "he") {
  const boards = loadBoards(language);
  const index = boards.findIndex(b => b.id === board.id);
  if (index >= 0) {
    boards[index] = board;
  } else {
    boards.push(board);
  }
  saveBoards(boards, language);
}

export function deleteBoard(boardId: string, language: "he" | "en" | "ru" | "ar" = "he") {
  const boards = loadBoards(language).filter(b => b.id !== boardId);
  saveBoards(boards, language);
}

export function resetToDefaultBoard(language: "he" | "en" | "ru" | "ar" = "he", gender: "male" | "female" | "neutral" = "neutral") {
  localStorage.removeItem(getBoardsStorageKey(language));
  return [getNewDefaultBoard(language, gender)];
}

// Edit a category within a board
export function updateCategory(
  board: CommunicationBoardData,
  categoryId: string,
  updates: Partial<Pick<CategoryCell, 'name' | 'icon' | 'emoji' | 'color' | 'showEmoji'>>
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell?.type === 'category' && cell.id === categoryId) {
        return { ...cell, ...updates };
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Edit any cell (category or utterance) on the main board by its ID
export function updateCell(
  board: CommunicationBoardData,
  cellId: string,
  updates: { name?: string; text?: string; icon?: string; emoji?: string; color?: string; showEmoji?: boolean }
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell && cell.id === cellId) {
        if (cell.type === 'category') {
          const newName = updates.name ?? updates.text ?? cell.name;
          return { ...cell, name: newName, icon: updates.icon, emoji: updates.emoji, color: updates.color ?? cell.color, showEmoji: updates.showEmoji };
        } else {
          const newText = updates.text ?? updates.name ?? cell.text;
          return { ...cell, text: newText, icon: updates.icon, emoji: updates.emoji, color: updates.color ?? cell.color, showEmoji: updates.showEmoji };
        }
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Delete a category from a board
export function deleteCategory(
  board: CommunicationBoardData,
  categoryId: string
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell?.type === 'category' && cell.id === categoryId) {
        return null;
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Delete any cell (category or utterance) from the main board by its ID
export function deleteCell(
  board: CommunicationBoardData,
  cellId: string
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell && cell.id === cellId) {
        return null;
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Add a new category to a board
export function addCategory(
  board: CommunicationBoardData,
  category: CategoryCell
): CommunicationBoardData {
  // Find first empty slot
  for (let r = 0; r < board.cells.length; r++) {
    for (let c = 0; c < board.cells[r].length; c++) {
      if (!board.cells[r][c]) {
        const newCells = [...board.cells.map(row => [...row])];
        newCells[r][c] = category;
        return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
      }
    }
  }
  // If no empty slot, add new row
  const newRow: BoardCell[] = Array(board.columns).fill(null);
  newRow[0] = category;
  return {
    ...board,
    rows: board.rows + 1,
    cells: [...board.cells, newRow],
    updatedAt: new Date().toISOString(),
  };
}

// Update an utterance within a category
export function updateUtterance(
  board: CommunicationBoardData,
  categoryId: string,
  utteranceId: string,
  updates: Partial<Pick<UtteranceCell, 'text' | 'icon' | 'emoji' | 'color' | 'showEmoji'>>
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell?.type === 'category' && cell.id === categoryId) {
        return {
          ...cell,
          utterances: cell.utterances.map(u =>
            u.id === utteranceId ? { ...u, ...updates } : u
          ),
        };
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Delete an utterance from a category
export function deleteUtterance(
  board: CommunicationBoardData,
  categoryId: string,
  utteranceId: string
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell?.type === 'category' && cell.id === categoryId) {
        return {
          ...cell,
          utterances: cell.utterances.filter(u => u.id !== utteranceId),
        };
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Add an utterance to a category
export function addUtterance(
  board: CommunicationBoardData,
  categoryId: string,
  utterance: UtteranceCell
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell?.type === 'category' && cell.id === categoryId) {
        return {
          ...cell,
          utterances: [...cell.utterances, utterance],
        };
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Reorder categories within a board
export function reorderCategory(
  board: CommunicationBoardData,
  fromRow: number,
  fromCol: number,
  toRow: number,
  toCol: number
): CommunicationBoardData {
  const newCells = board.cells.map(row => [...row]);
  const temp = newCells[fromRow][fromCol];
  newCells[fromRow][fromCol] = newCells[toRow][toCol];
  newCells[toRow][toCol] = temp;
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}

// Reorder utterances within a category
export function reorderUtterance(
  board: CommunicationBoardData,
  categoryId: string,
  fromIndex: number,
  toIndex: number
): CommunicationBoardData {
  const newCells = board.cells.map(row =>
    row.map(cell => {
      if (cell?.type === 'category' && cell.id === categoryId) {
        const newUtterances = [...cell.utterances];
        const [moved] = newUtterances.splice(fromIndex, 1);
        newUtterances.splice(toIndex, 0, moved);
        return { ...cell, utterances: newUtterances };
      }
      return cell;
    })
  );
  return { ...board, cells: newCells, updatedAt: new Date().toISOString() };
}
